/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';
import s from './styles.module.scss';
import { useRouter } from 'next/router';
import { ReadSurah } from '@/infrastructure/class/read-surah';
import ReadSurahsListItem from './item/index';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import AppTitle from '@/components/common/title';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import { Button } from '@mui/material';
import { ExpandMoreRounded } from '@mui/icons-material';
import { paginate } from '@/utils/helpers/paginate';
import { readSurahPaginationLimit } from '@/infrastructure/const/pagination';
import Link from 'next/link';

interface IReadSurahsListProps {
  items: IReadSurah[];
  mainTitle?: boolean;
  bordered?: boolean;
  withPagination?: boolean;
  serverPage?: number;
  className?: string;
}

const ReadSurahsList: React.FC<IReadSurahsListProps> = (props) => {
  const {
    items,
    serverPage,
    mainTitle = false,
    bordered = false,
    withPagination = false,
    className = '',
  } = props;

  const router = useRouter();
  const { locale, query } = router;
  const { page = 1 } = query;

  const [localPage, setLocalPage] = useState<number>(Number(page));
  const [localItems, setLocalItems] = useState<IReadSurah[]>(
    paginate(items, readSurahPaginationLimit, localPage)
  );
  const pageCount = Math.ceil(items.length / readSurahPaginationLimit);

  const hasMoreItems =
    localItems.length !== items.length && Number(page) < pageCount;

  const hasItems = !!items.length;

  const { t: tCommon } = useTranslation('common');
  const { t: tButtons } = useTranslation('buttons');

  const title = `${tCommon('surahs')}`;

  const wrapperClassNames = classNames(s['wrapper'], className);
  const noItemsClassNames = classNames(s['no-item'], {
    [s['has-items']]: hasItems,
  });
  const titleClassNames = classNames(s['title'], {
    [s['bordered']]: bordered,
  });

  const readSurahs = useMemo<ReadSurah[]>(() => {
    return sortArrayByFieldName(
      localItems.map((readSurah) => new ReadSurah(readSurah, locale)),
      'index'
    );
  }, [localItems, locale]);

  const onShowMore = async () => {
    setLocalPage(localPage + 1);
    const newItems = paginate(items, readSurahPaginationLimit, localPage + 1);
    setLocalItems([...localItems, ...newItems]);
  };

  const onSeeAll = async () => {
    setLocalItems(items);
  };

  useEffect(() => {
    setLocalPage(1);
    const newItems = paginate(items, readSurahPaginationLimit, 1);
    setLocalItems(newItems);
  }, [items]);

  return (
    <div className={wrapperClassNames}>
      <div className={titleClassNames}>
        <AppTitle title={title} main={mainTitle} />
        {hasMoreItems && (
          <span onClick={onSeeAll} className={s['link']}>
            {tButtons('seeAll')}
          </span>
        )}
      </div>
      {hasItems ? (
        <div>
          <table className={s['table']}>
            <tbody className={s['table-body']}>
              {readSurahs.map((readSurah, index) => (
                <ReadSurahsListItem
                  key={readSurah.slug}
                  data={readSurah}
                  needTopBorder={index < 2}
                />
              ))}
            </tbody>
          </table>
          {hasMoreItems && (
            <div className={s['bottom']}>
              {withPagination ? (
                <Link
                  href={{
                    query: {
                      page: Number(localPage) + 1,
                    },
                  }}
                  shallow={true}
                  scroll={false}
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  <Button
                    id="pagination-button"
                    aria-controls="pagination-button"
                    color="error"
                    onClick={onShowMore}
                    size="medium"
                    classes={{
                      root: s['pagination-btn'],
                    }}
                    endIcon={<ExpandMoreRounded />}
                  >
                    {tButtons('showMore')}
                  </Button>
                </Link>
              ) : (
                <Button
                  id="pagination-button"
                  aria-controls="pagination-button"
                  color="error"
                  onClick={onShowMore}
                  size="medium"
                  classes={{
                    root: s['pagination-btn'],
                  }}
                  endIcon={<ExpandMoreRounded />}
                >
                  {tButtons('showMore')}
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={noItemsClassNames}>
          <AppTitle title={tCommon('noItems')} />
        </div>
      )}
    </div>
  );
};

export default ReadSurahsList;
